import React from 'react';
import { useAuth } from '../../packages/auth-client';
import { FeatureCard, HubHeading, NoteBar } from '../../components/hub/Hub';
import { usePageTitle } from '../../hooks/usePageTitle';

/**
 * Tenant (workspace) admin: role "admin" in the viewer's tenant, or a
 * superadmin (the same test ProjectDetailPage uses). Presentation only — each
 * destination's backend route enforces its own permission.
 */
export function useIsWorkspaceAdmin(): boolean {
    const { user, isSuperAdmin } = useAuth();
    return isSuperAdmin() || (user?.roles ?? []).includes('admin');
}

interface Entry { to: string; label: string; hint: string; badge?: string }

// Workspace cards, admin-only (concept renderSettings order: billing, AI
// provider, people, then the rest). Connections is open to every member.
const WORKSPACE_ADMIN: Entry[] = [
    { to: '/billing', label: 'Billing & usage', hint: 'Plan, payment, invoices and usage.', badge: 'Owner / billing admin' },
    { to: '/admin/ai', label: 'AI provider', hint: "The workspace's Anthropic API key.", badge: 'Workspace admin' },
    { to: '/people', label: 'People, roles & access', hint: 'Members, invitations and roles.', badge: 'Access' },
    { to: '/settings/workspace', label: 'Workspace details', hint: 'Name and identifier of this workspace.', badge: 'Workspace admin' },
    { to: '/settings/workspace#lifecycle', label: 'Data & lifecycle', hint: 'Workspace deletion and your account.', badge: 'Workspace admin' },
];

// API keys moved from /profile?tab=apikeys to /connections (U25).
const CONNECTIONS: Entry = { to: '/connections', label: 'AI-agent connections', hint: 'Your API keys for MCP and AI agents, bounded by your permissions.', badge: 'Integrations' };

// One entry per ProfilePage tab (PROFILE_TAB_IDS), then help; the test keeps them in step.
const PERSONAL: Entry[] = [
    { to: '/profile?tab=info', label: 'Profile', hint: 'Your name and verified email.' },
    { to: '/profile?tab=avatar', label: 'Photo', hint: 'Your avatar.' },
    { to: '/profile?tab=password', label: 'Password', hint: 'Change your password.' },
    { to: '/profile?tab=devices', label: 'Trusted devices', hint: 'Devices that skip the sign-in code.' },
    { to: '/profile?tab=notifications', label: 'Email & notifications', hint: 'Email digest cadence.' },
    { to: '/profile?tab=assistant', label: 'Assistant & calendars', hint: 'Briefings, quiet hours, channels and calendars.' },
    { to: '/profile?tab=delete', label: 'Delete account', hint: 'Schedule deletion, with a recovery window.' },
    { to: '/help', label: 'Help & support', hint: 'Contact the team, your tickets and docs.' },
    { to: '/health', label: 'Service status', hint: 'Is the API up right now.' },
];

function Cards({ entries }: { entries: Entry[] }) {
    return (
        <ul className="hub-feature-grid">
            {entries.map((e) => (
                <li key={e.to}>
                    <FeatureCard to={e.to} title={e.label} description={e.hint} badge={e.badge} />
                </li>
            ))}
        </ul>
    );
}

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
    return (
        <section className="hub-section" aria-labelledby={id}>
            <h2 id={id} className="hub-section-label">{title}</h2>
            {children}
        </section>
    );
}

/** /settings — Workspace settings (admin cards + connections) kept apart from Personal & support (everyone). */
export function SettingsHome() {
    usePageTitle('Settings & help');
    const admin = useIsWorkspaceAdmin();
    const { user, isImpersonating } = useAuth();
    const monitoring = user?.roles.includes('admin') && !user.is_project_restricted && !user.is_task_restricted && !user.impersonated_by && !isImpersonating;
    const monitoringEntries: Entry[] = monitoring ? [{ to: '/settings/delivery', label: 'Delivery evidence', hint: 'GitHub merges, deployments and human production verification.', badge: 'Integrations' }, { to: '/settings/monitoring', label: 'Monitoring', hint: 'Sentry connections, delivery status and incoming issues.', badge: 'Preview' }] : [];
    return (
        <div className="hub-page">
            <HubHeading
                eyebrow="Settings"
                title="Everything has a place."
                description="Workspace settings affect everyone in your workspace. Personal settings affect only you."
            />
            <Section id="settings-workspace" title="Workspace">
                <Cards entries={admin ? [...WORKSPACE_ADMIN, ...monitoringEntries, CONNECTIONS] : [CONNECTIONS]} />
                {!admin && (
                    <p className="hub-description" style={{ marginTop: 'var(--space-3)' }}>
                        Workspace settings (people, billing, AI provider) are managed by your workspace admins.
                    </p>
                )}
            </Section>
            <Section id="settings-personal" title="Personal & support">
                <Cards entries={PERSONAL} />
            </Section>
            <NoteBar>
                Platform-wide tenant management, permission-group administration, audit logs, and the support inbox
                belong to the separate platform console. A workspace owner does not automatically receive platform privileges.
            </NoteBar>
        </div>
    );
}
